import { supabase } from '../lib/supabase'
import { AIBudget, BudgetCategory, BudgetRecommendation } from '../types/ai-budget'
import { categoryMappingService } from './categoryMappingService'
import { Transaction } from '../types/dashboard'

interface CreateBudgetParams {
  userId: string
  sessionId?: string
  name: string
  description?: string
  monthlyIncome: number
  budgetRecommendation: BudgetRecommendation
}

interface BudgetServiceReturn {
  saveBudget: (params: CreateBudgetParams) => Promise<AIBudget>
  getUserBudgets: (userId: string) => Promise<AIBudget[]>
  updateBudgetStatus: (budgetId: string, status: 'active' | 'paused' | 'completed') => Promise<void>
  deleteBudget: (budgetId: string) => Promise<void>
  getBudgetById: (budgetId: string) => Promise<AIBudget | null>
  getBudgetTracking: (budgetId: string, monthYear?: string) => Promise<any[]>
  updateBudgetTrackingFromTransaction: (transaction: Transaction, action: 'add' | 'edit' | 'delete', userId: string, oldTransaction?: Transaction) => Promise<void>
  recalculateBudgetTracking: (budgetId: string, monthYear?: string) => Promise<void>
  syncTransactionsWithBudget: (budgetId: string, transactions: Transaction[]) => Promise<void>
  getActiveBudget: (userId: string) => Promise<AIBudget | null>
  handleMonthRollover: (userId: string) => Promise<void>
}

class BudgetService {
  /**
   * Save an AI-generated budget recommendation to the database
   */
  async saveBudget(params: CreateBudgetParams): Promise<AIBudget> {
    console.log('💰 BudgetService: Saving budget to database...', {
      userId: params.userId,
      sessionId: params.sessionId,
      name: params.name,
      monthlyIncome: params.monthlyIncome
    })

    try {
      // Prepare budget categories for database storage
      const budgetCategories: BudgetCategory[] = params.budgetRecommendation.recommended_budget.map(category => ({
        name: category.name,
        allocated: category.allocated,
        percentage: category.percentage,
        type: category.type,
        description: category.description
      }))

      console.log('📋 BudgetService: Preparing to save', budgetCategories.length, 'categories')
      console.log('📋 BudgetService: Categories being saved:', budgetCategories.map(c => `${c.name}: $${c.allocated} (${c.percentage}%)`))

      // Calculate savings and investment goals
      const savingsGoal = budgetCategories
        .filter(cat => cat.type === 'savings')
        .reduce((sum, cat) => sum + cat.allocated, 0)

      const investmentGoal = budgetCategories
        .filter(cat => cat.type === 'investment')
        .reduce((sum, cat) => sum + cat.allocated, 0)

      // Prepare insert data
      const insertData = {
        user_id: params.userId,
        session_id: params.sessionId || null,
        name: params.name,
        description: params.description || null,
        monthly_income: params.monthlyIncome,
        budget_categories: budgetCategories,
        savings_goal: savingsGoal,
        investment_goal: investmentGoal,
        ai_insights: params.budgetRecommendation.reasoning,
        status: 'active' as const
      }

      console.log('💰 BudgetService: Insert data prepared with', insertData.budget_categories.length, 'categories')

      // Insert into database
      const { data: budget, error } = await supabase
        .from('ai_budgets')
        .insert([insertData])
        .select()
        .single()

      if (error) {
        console.error('❌ BudgetService: Database error:', error)
        throw new Error(`Failed to save budget: ${error.message}`)
      }

      console.log('✅ BudgetService: Budget saved successfully:', budget.id)
      console.log('✅ BudgetService: Saved budget has', budget.budget_categories?.length || 0, 'categories')
      
      if (budget.budget_categories && budget.budget_categories.length !== budgetCategories.length) {
        console.error('⚠️ BudgetService: MISMATCH! Tried to save', budgetCategories.length, 'categories but database returned', budget.budget_categories.length)
        console.error('⚠️ BudgetService: Sent:', budgetCategories.map(c => c.name))
        console.error('⚠️ BudgetService: Received:', budget.budget_categories.map((c: any) => c.name))
      }

      // Create initial budget tracking entries for the current month
      await this.createInitialBudgetTracking(budget.id, budgetCategories)

      return budget as AIBudget
    } catch (error) {
      console.error('❌ BudgetService: Error saving budget:', error)
      throw error
    }
  }

  /**
   * Create initial budget tracking entries for the current month
   */
  private async createInitialBudgetTracking(budgetId: string, categories: BudgetCategory[]): Promise<void> {
    const currentMonthYear = new Date().toISOString().slice(0, 7) // Format: YYYY-MM

    const trackingEntries = categories.map(category => ({
      budget_id: budgetId,
      category: category.name,
      allocated_amount: category.allocated,
      spent_amount: 0,
      month_year: currentMonthYear
    }))

    console.log('📊 BudgetService: Creating initial tracking entries for month:', currentMonthYear)

    const { error } = await supabase
      .from('budget_tracking')
      .insert(trackingEntries)

    if (error) {
      console.warn('⚠️ BudgetService: Failed to create tracking entries:', error.message)
      // Don't throw here - budget is already saved, tracking is optional
    } else {
      console.log('✅ BudgetService: Budget tracking entries created')
    }
  }

  /**
   * Get all budgets for a user
   */
  async getUserBudgets(userId: string): Promise<AIBudget[]> {
    console.log('📋 BudgetService: Fetching budgets for user:', userId)

    try {
      const { data: budgets, error } = await supabase
        .from('ai_budgets')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })

      if (error) {
        console.error('❌ BudgetService: Error fetching budgets:', error)
        throw new Error(`Failed to fetch budgets: ${error.message}`)
      }

      console.log(`✅ BudgetService: Found ${budgets?.length || 0} budgets`)
      
      // Log category counts for each budget
      budgets?.forEach((budget, index) => {
        console.log(`📊 Budget ${index + 1}: "${budget.name}" has ${budget.budget_categories?.length || 0} categories`)
        if (budget.budget_categories) {
          console.log(`   Categories:`, budget.budget_categories.map((c: any) => c.name).join(', '))
        }
      })
      
      return budgets as AIBudget[] || []
    } catch (error) {
      console.error('❌ BudgetService: Error in getUserBudgets:', error)
      throw error
    }
  }

  /**
   * Update budget status
   */
  async updateBudgetStatus(budgetId: string, status: 'active' | 'paused' | 'completed'): Promise<void> {
    console.log(`🔄 BudgetService: Updating budget ${budgetId} status to:`, status)

    try {
      const { error } = await supabase
        .from('ai_budgets')
        .update({ 
          status,
          updated_at: new Date().toISOString()
        })
        .eq('id', budgetId)

      if (error) {
        console.error('❌ BudgetService: Error updating budget status:', error)
        throw new Error(`Failed to update budget status: ${error.message}`)
      }

      console.log('✅ BudgetService: Budget status updated successfully')
    } catch (error) {
      console.error('❌ BudgetService: Error in updateBudgetStatus:', error)
      throw error
    }
  }

  /**
   * Delete a budget and associated tracking data
   */
  async deleteBudget(budgetId: string): Promise<void> {
    console.log('🗑️ BudgetService: Deleting budget:', budgetId)

    try {
      // Delete budget (cascade will handle tracking entries due to foreign key)
      const { error } = await supabase
        .from('ai_budgets')
        .delete()
        .eq('id', budgetId)

      if (error) {
        console.error('❌ BudgetService: Error deleting budget:', error)
        throw new Error(`Failed to delete budget: ${error.message}`)
      }

      console.log('✅ BudgetService: Budget deleted successfully')
    } catch (error) {
      console.error('❌ BudgetService: Error in deleteBudget:', error)
      throw error
    }
  }

  /**
   * Get a specific budget by ID
   */
  async getBudgetById(budgetId: string): Promise<AIBudget | null> {
    console.log('🔍 BudgetService: Fetching budget by ID:', budgetId)

    try {
      const { data: budget, error } = await supabase
        .from('ai_budgets')
        .select('*')
        .eq('id', budgetId)
        .single()

      if (error) {
        if (error.code === 'PGRST116') {
          // No rows returned
          console.log('📭 BudgetService: Budget not found')
          return null
        }
        console.error('❌ BudgetService: Error fetching budget:', error)
        throw new Error(`Failed to fetch budget: ${error.message}`)
      }

      console.log('✅ BudgetService: Budget found')
      return budget as AIBudget
    } catch (error) {
      console.error('❌ BudgetService: Error in getBudgetById:', error)
      throw error
    }
  }

  /**
   * Get budget tracking data for a specific month
   */
  async getBudgetTracking(budgetId: string, monthYear?: string): Promise<any[]> {
    const targetMonth = monthYear || new Date().toISOString().slice(0, 7)
    
    console.log(`📊 BudgetService: Fetching tracking for budget ${budgetId}, month: ${targetMonth}`)

    try {
      const { data, error } = await supabase
        .from('budget_tracking')
        .select('*')
        .eq('budget_id', budgetId)
        .eq('month_year', targetMonth)

      if (error) {
        console.error('❌ BudgetService: Error fetching tracking:', error)
        throw new Error(`Failed to fetch budget tracking: ${error.message}`)
      }

      console.log(`✅ BudgetService: Found ${data?.length || 0} tracking entries`)
      return data || []
    } catch (error) {
      console.error('❌ BudgetService: Error in getBudgetTracking:', error)
      throw error
    }
  }

  /**
   * Get the active budget for a user
   */
  async getActiveBudget(userId: string): Promise<AIBudget | null> {
    console.log('🔍 BudgetService: Fetching active budget for user:', userId)

    try {
      const { data: budget, error } = await supabase
        .from('ai_budgets')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1)
        .single()

      if (error) {
        if (error.code === 'PGRST116') {
          // No rows returned
          console.log('📭 BudgetService: No active budget found')
          return null
        }
        console.error('❌ BudgetService: Error fetching active budget:', error)
        throw new Error(`Failed to fetch active budget: ${error.message}`)
      }

      console.log('✅ BudgetService: Active budget found:', budget.name)
      return budget as AIBudget
    } catch (error) {
      console.error('❌ BudgetService: Error in getActiveBudget:', error)
      throw error
    }
  }

  /**
   * Update budget tracking based on a transaction
   */
  async updateBudgetTrackingFromTransaction(
    transaction: Transaction, 
    action: 'add' | 'edit' | 'delete', 
    userId: string,
    oldTransaction?: Transaction
  ): Promise<void> {
    console.log(`💰 BudgetService: Updating budget tracking for transaction ${action}:`, {
      transactionId: transaction.id,
      category: transaction.category,
      amount: transaction.amount,
      type: transaction.type
    })

    // Only process expense transactions (income doesn't count against budgets)
    if (transaction.type !== 'expense') {
      console.log('⏭️ BudgetService: Skipping income transaction for budget tracking')
      return
    }

    if (!transaction.category) {
      console.log('⚠️ BudgetService: Transaction has no category, skipping budget update')
      return
    }

    try {
      // Get the transaction's month/year
      const transactionDate = new Date(transaction.date)
      const monthYear = transactionDate.toISOString().slice(0, 7) // Format: YYYY-MM

      // Find active budget for the user
      const budget = await this.getActiveBudget(userId)
      if (!budget) {
        console.log('📭 BudgetService: No active budget found, skipping tracking update')
        return
      }

      // Map transaction category to budget category
      const budgetCategory = categoryMappingService.mapTransactionToBudgetCategory(
        transaction.category,
        budget.budget_categories
      )

      if (!budgetCategory) {
        console.log(`⚠️ BudgetService: Could not map transaction category "${transaction.category}" to any budget category`)
        return
      }

      console.log(`🎯 BudgetService: Mapped "${transaction.category}" to budget category "${budgetCategory.name}"`)

      // Handle different actions
      switch (action) {
        case 'add':
          await this.addTransactionToTracking(budget.id, budgetCategory.name, transaction.amount, monthYear)
          break
        case 'edit':
          if (oldTransaction) {
            // Subtract old amount from old category (if different)
            if (oldTransaction.category && oldTransaction.category !== transaction.category) {
              const oldBudgetCategory = categoryMappingService.mapTransactionToBudgetCategory(
                oldTransaction.category,
                budget.budget_categories
              )
              if (oldBudgetCategory) {
                await this.addTransactionToTracking(budget.id, oldBudgetCategory.name, -oldTransaction.amount, monthYear)
              }
            } else if (oldTransaction.category === transaction.category) {
              // Same category, just update the difference
              const amountDifference = transaction.amount - oldTransaction.amount
              await this.addTransactionToTracking(budget.id, budgetCategory.name, amountDifference, monthYear)
            }
          }
          break
        case 'delete':
          await this.addTransactionToTracking(budget.id, budgetCategory.name, -transaction.amount, monthYear)
          break
      }

      console.log('✅ BudgetService: Budget tracking updated successfully')
    } catch (error) {
      console.error('❌ BudgetService: Error updating budget tracking:', error)
      // Don't throw - budget tracking is not critical to transaction operations
    }
  }

  /**
   * Add transaction amount to budget tracking
   */
  private async addTransactionToTracking(
    budgetId: string, 
    categoryName: string, 
    amount: number, 
    monthYear: string
  ): Promise<void> {
    console.log(`📊 BudgetService: Adding ${amount} to ${categoryName} tracking for ${monthYear}`)

    try {
      // First, ensure tracking entry exists for this category and month
      await this.ensureTrackingEntryExists(budgetId, categoryName, monthYear)

      // Get current spent amount
      const { data: currentTracking, error: fetchError } = await supabase
        .from('budget_tracking')
        .select('spent_amount')
        .eq('budget_id', budgetId)
        .eq('category', categoryName)
        .eq('month_year', monthYear)
        .single()

      if (fetchError) {
        console.error('❌ BudgetService: Error fetching current tracking:', fetchError)
        throw new Error(`Failed to fetch current tracking: ${fetchError.message}`)
      }

      // Calculate new spent amount
      const newSpentAmount = Math.max(0, (currentTracking?.spent_amount || 0) + amount)

      // Update the spent amount
      const { error } = await supabase
        .from('budget_tracking')
        .update({ 
          spent_amount: newSpentAmount,
          updated_at: new Date().toISOString()
        })
        .eq('budget_id', budgetId)
        .eq('category', categoryName)
        .eq('month_year', monthYear)

      if (error) {
        console.error('❌ BudgetService: Error updating tracking amount:', error)
        throw new Error(`Failed to update tracking amount: ${error.message}`)
      }

      console.log(`✅ BudgetService: Tracking amount updated: ${currentTracking?.spent_amount || 0} + ${amount} = ${newSpentAmount}`)
    } catch (error) {
      console.error('❌ BudgetService: Error in addTransactionToTracking:', error)
      throw error
    }
  }

  /**
   * Ensure a tracking entry exists for the given category and month
   */
  private async ensureTrackingEntryExists(
    budgetId: string, 
    categoryName: string, 
    monthYear: string
  ): Promise<void> {
    console.log(`🔍 BudgetService: Ensuring tracking entry exists for ${categoryName} in ${monthYear}`)

    try {
      // Check if entry exists
      const { data: existing, error: checkError } = await supabase
        .from('budget_tracking')
        .select('id')
        .eq('budget_id', budgetId)
        .eq('category', categoryName)
        .eq('month_year', monthYear)
        .single()

      if (checkError && checkError.code !== 'PGRST116') {
        console.error('❌ BudgetService: Error checking tracking entry:', checkError)
        throw checkError
      }

      if (!existing) {
        // Entry doesn't exist, create it
        console.log('📝 BudgetService: Creating new tracking entry')

        // Get the budget to find the allocated amount for this category
        const budget = await this.getBudgetById(budgetId)
        if (!budget) {
          throw new Error('Budget not found')
        }

        const budgetCategory = budget.budget_categories.find(cat => cat.name === categoryName)
        if (!budgetCategory) {
          throw new Error(`Budget category "${categoryName}" not found`)
        }

        const { error: insertError } = await supabase
          .from('budget_tracking')
          .insert({
            budget_id: budgetId,
            category: categoryName,
            allocated_amount: budgetCategory.allocated,
            spent_amount: 0,
            month_year: monthYear
          })

        if (insertError) {
          console.error('❌ BudgetService: Error creating tracking entry:', insertError)
          throw new Error(`Failed to create tracking entry: ${insertError.message}`)
        }

        console.log('✅ BudgetService: Tracking entry created successfully')
      } else {
        console.log('✅ BudgetService: Tracking entry already exists')
      }
    } catch (error) {
      console.error('❌ BudgetService: Error in ensureTrackingEntryExists:', error)
      throw error
    }
  }

  /**
   * Recalculate budget tracking based on all transactions for a month
   */
  async recalculateBudgetTracking(budgetId: string, monthYear?: string): Promise<void> {
    const targetMonth = monthYear || new Date().toISOString().slice(0, 7)
    
    console.log(`🔄 BudgetService: Recalculating budget tracking for ${budgetId} in ${targetMonth}`)

    try {
      // Get the budget
      const budget = await this.getBudgetById(budgetId)
      if (!budget) {
        throw new Error('Budget not found')
      }

      // Reset all tracking entries for this month to 0
      const { error: resetError } = await supabase
        .from('budget_tracking')
        .update({ spent_amount: 0 })
        .eq('budget_id', budgetId)
        .eq('month_year', targetMonth)

      if (resetError) {
        console.error('❌ BudgetService: Error resetting tracking:', resetError)
        throw new Error(`Failed to reset tracking: ${resetError.message}`)
      }

      console.log('✅ BudgetService: Budget tracking recalculated successfully')
    } catch (error) {
      console.error('❌ BudgetService: Error in recalculateBudgetTracking:', error)
      throw error
    }
  }

  /**
   * Check and handle month rollover for active budgets
   */
  async handleMonthRollover(userId: string): Promise<void> {
    console.log('📅 BudgetService: Checking for month rollover...')

    try {
      const activeBudget = await this.getActiveBudget(userId)
      if (!activeBudget) {
        console.log('📅 BudgetService: No active budget found, skipping rollover')
        return
      }

      const currentMonth = new Date().toISOString().slice(0, 7) // Format: YYYY-MM
      
      // Check if tracking entries exist for current month
      const { data: currentTracking, error } = await supabase
        .from('budget_tracking')
        .select('id')
        .eq('budget_id', activeBudget.id)
        .eq('month_year', currentMonth)
        .limit(1)

      if (error) {
        console.error('❌ BudgetService: Error checking current month tracking:', error)
        return
      }

      if (!currentTracking || currentTracking.length === 0) {
        console.log(`📅 BudgetService: No tracking entries for ${currentMonth}, creating new ones`)
        
        // Create new tracking entries for current month
        await this.createInitialBudgetTracking(activeBudget.id, activeBudget.budget_categories)
        console.log('✅ BudgetService: Month rollover completed successfully')
      } else {
        console.log(`📅 BudgetService: Tracking entries already exist for ${currentMonth}`)
      }
    } catch (error) {
      console.error('❌ BudgetService: Error in handleMonthRollover:', error)
    }
  }

  /**
   * Sync existing transactions with a budget (one-time operation)
   */
  async syncTransactionsWithBudget(budgetId: string, transactions: Transaction[]): Promise<void> {
    console.log(`🔄 BudgetService: Syncing ${transactions.length} transactions with budget ${budgetId}`)

    try {
      // Get the budget
      const budget = await this.getBudgetById(budgetId)
      if (!budget) {
        throw new Error('Budget not found')
      }

      // Group transactions by month
      const transactionsByMonth = transactions.reduce((acc, transaction) => {
        if (transaction.type !== 'expense' || !transaction.category) {
          return acc
        }

        const monthYear = new Date(transaction.date).toISOString().slice(0, 7)
        if (!acc[monthYear]) {
          acc[monthYear] = []
        }
        acc[monthYear].push(transaction)
        return acc
      }, {} as Record<string, Transaction[]>)

      // Process each month
      for (const [monthYear, monthTransactions] of Object.entries(transactionsByMonth)) {
        console.log(`📅 BudgetService: Processing ${monthTransactions.length} transactions for ${monthYear}`)

        // Group by budget category
        const transactionsByCategory = monthTransactions.reduce((acc, transaction) => {
          const budgetCategory = categoryMappingService.mapTransactionToBudgetCategory(
            transaction.category!,
            budget.budget_categories
          )

          if (budgetCategory) {
            if (!acc[budgetCategory.name]) {
              acc[budgetCategory.name] = 0
            }
            acc[budgetCategory.name] += transaction.amount
          }

          return acc
        }, {} as Record<string, number>)

        // Update tracking for each category
        for (const [categoryName, totalAmount] of Object.entries(transactionsByCategory)) {
          await this.ensureTrackingEntryExists(budgetId, categoryName, monthYear)
          
          const { error } = await supabase
            .from('budget_tracking')
            .update({ 
              spent_amount: totalAmount,
              updated_at: new Date().toISOString()
            })
            .eq('budget_id', budgetId)
            .eq('category', categoryName)
            .eq('month_year', monthYear)

          if (error) {
            console.error(`❌ BudgetService: Error updating ${categoryName} tracking:`, error)
          }
        }
      }

      console.log('✅ BudgetService: Transaction sync completed successfully')
    } catch (error) {
      console.error('❌ BudgetService: Error in syncTransactionsWithBudget:', error)
      throw error
    }
  }
}

// Create and export singleton instance
export const budgetService = new BudgetService()

// Export the service interface for type checking
export type { BudgetServiceReturn, CreateBudgetParams }

